import { Navigate, type RouteObject } from 'react-router';
import AppRoot from './app-root';
import { RequireAdmin, RequireAnonymous, RequireLinkToken, RequireSignedIn, registrationOpenLoader } from './core/session/guards';
import { CLAIMS_OFFLINE_PANEL, READABLE_OFFLINE } from './core/pwa/works-offline';
import { contextRoutes, projectRoutes } from './features/containers/containers.routes';
import { archiveRoutes } from './features/archive/archive.routes';
import { routes as ticklerRoutes } from './features/tickler/tickler.routes';
import { routes as tagsRoutes } from './features/tags/tags.routes';
import { routes as feedsRoutes } from './features/feeds/feeds.routes';
import { routes as accountRoutes } from './features/account/account.routes';
import { routes as recurringRoutes } from './features/recurring/recurring.routes';
import { routes as reviewRoutes } from './features/review/review.routes';
import { routes as searchRoutes } from './features/search/search.routes';
import { routes as statsRoutes } from './features/stats/stats.routes';
import { routes as adminRoutes } from './features/admin/admin.routes';
import { routes as importRoutes } from './features/import/import.routes';
import { routes as settingsRoutes } from './features/settings/settings.routes';
import { routes as helpRoutes } from './features/help/help.routes';
import { routes as unsentRoutes } from './features/unsent/unsent.routes';

/**
 * REACT ROUTER'S `lazy`, NOT `React.lazy` — and the difference is not stylistic.
 *
 * `React.lazy` in an `element` SUSPENDS during the navigation update, which React treats as a
 * component suspending in response to synchronous input: it throws error 311 and React Router
 * replaces the whole page with its default error screen. The first e2e run after the shell landed
 * showed exactly that — "Unexpected Application Error!" where the sign-in form should have been —
 * and it read as a missing control rather than a broken route.
 *
 * Angular's `loadComponent` resolved a route before rendering it. React Router's `lazy` does the
 * same thing: the router awaits the import as part of the navigation, so nothing suspends mid-
 * render. It is the real counterpart; `React.lazy` merely looks like one.
 */
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({ Component: (await load()).default });

/**
 * 034 T019 — the route table, after Angular's.
 *
 * THE GUARD IS ON THE SHELL ROUTE, SO EVERY CHILD INHERITS IT. `app.routes.ts` states the reason and
 * it is unchanged: protecting a new feature is not a step someone can forget. React Router's layout
 * routes reproduce it exactly — a child cannot opt out of the element its parent renders.
 *
 * **This table is incomplete ON PURPOSE, and the incompleteness is visible rather than papered
 * over.** Phase 2 delivers the shell and sign-in; the other eighteen route areas are Phase 3, and
 * each adds its own mount as it lands. 005 FR-015 forbids a placeholder route for a screen a later
 * story builds — that rule does not lapse because the reason a screen is missing has changed from
 * "not designed yet" to "not ported yet". An unported path 404s through the wildcard, which is the
 * honest answer and is exactly what the e2e suite will report until the area is done.
 *
 * The comments on each mount below are carried from the Angular table, because they encode
 * decisions from cycles 005, 011, 013, 014, 019, 031 and 033 rather than describing syntax.
 */
export const routes: RouteObject[] = [
  {
    /**
     * 034 Phase 4 — the PWA root, where Angular had a component wrapped around the router.
     *
     * `app-root.tsx` carries the update banner, the offline panel for every surface outside the
     * shell, and FR-013's navigation backstop. It is a PATHLESS route rather than a sibling of
     * `RouterProvider` because two of those three need the router: a navigation it can observe, and
     * an `Outlet` it can hide. Every top-level entry that used to be at this level is now a child of
     * it, and nothing else about them changed — the wildcard included.
     */
    element: <AppRoot />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [
          { path: '/sign-in', lazy: page(() => import('./features/auth/sign-in-page')) },
          {
            /**
             * 014 FR-003: outside the shell like sign-in, and additionally behind the availability read
             * — a closed instance has no front door. Not a child of the sign-in mount, because the URL
             * is /sign-up, not /sign-in/sign-up.
             */
            path: '/sign-up',
            loader: registrationOpenLoader,
            lazy: page(() => import('./features/auth/sign-up-page')),
          },
        ],
      },
      {
        /**
         * 018 US1/US2: password recovery and the verification link-click. The token rides the URL as a
         * query parameter — never a path segment (011's rule) — and decides request vs confirm mode.
         * `RequireLinkToken`, not `RequireAnonymous`: the token is the credential, so a link is followed
         * whatever session the browser is holding.
         *
         * Mounted (034): both screens read the token from `useSearchParams` themselves, so this route
         * object carries no query-parsing of its own — only the guard and the two lazy chunks.
         */
        element: <RequireLinkToken />,
        children: [
          {
            path: '/password-reset',
            lazy: page(() => import('./features/auth/password-reset-page')),
          },
          { path: '/verify-email', lazy: page(() => import('./features/auth/verify-email-page')) },
        ],
      },
      {
        /**
         * The shell. Everything behind it inherits the session guard, which is the whole point of
         * mounting it here rather than on each child.
         */
        path: '/',
        element: <RequireSignedIn />,
        children: [
          {
            // 036 (issue 219): the shell claims the offline panel itself, from a `useEffect` once
            // mounted — but that effect fires only after this route's LAZY module resolves, and
            // `app-root.tsx` decides whether to show its OWN panel on every render in between. This
            // `handle` is known synchronously from the URL, before the lazy import even starts, which
            // is what lets `AppRoot` skip its own panel for the whole window rather than race it.
            handle: { [CLAIMS_OFFLINE_PANEL]: true },
            lazy: page(() => import('./layout/layout')),
            children: [
              { index: true, element: <Navigate to="/home" replace /> },
              // 034 Phase 3: the first ported route area. Lazy, so home's chunk — the row, both
              // editors, the picker and the drag library — is not in the bundle a practitioner who
              // lands on /sign-in downloads.
              //
              // 036 (issue 208): READABLE_OFFLINE — home draws a stored view with no connection
              // (FR-021) rather than falling back to 021's offline panel.
              {
                path: 'home',
                handle: { [READABLE_OFFLINE]: true },
                lazy: page(() => import('./features/home/home-page')),
              },
              // 034 Phase 3: the containers area — a list per kind and the detail page they share.
              // `children` rather than two flat paths, because `/projects` and `/projects/:id` are one
              // area with one lazy chunk; `containers.routes.tsx` states why they are two arrays.
              { path: 'projects', children: projectRoutes },
              { path: 'contexts', children: contextRoutes },
              // 034 Phase 3: the completed archive. `children` for the same reason the containers area
              // uses it — one lazy chunk, and `archive.routes.tsx` owns the narrowing's shape.
              { path: 'done', children: archiveRoutes },
              // 034 Phase 3: the tickler file.
              { path: 'tickler', children: ticklerRoutes },
              // 034 Phase 3: the tag cloud and one tag's own page.
              { path: 'tags', children: tagsRoutes },
              // 034 Phase 3: one of the two maintenance surfaces, reached from /settings and never the
              // sidebar — 019's clarification, which the route table has no way to express and the
              // layout's navigation does.
              { path: 'feeds', children: feedsRoutes },
              { path: 'account', children: accountRoutes },
              { path: 'recurring', children: recurringRoutes },
              { path: 'review', children: reviewRoutes },
              { path: 'search', children: searchRoutes },
              { path: 'stats', children: statsRoutes },
              // The admin area carries its OWN guard on the mount, so a screen added inside it cannot
              // forget one — the same construction the shell uses, one level down (014 FR-016).
              { path: 'admin', element: <RequireAdmin />, children: adminRoutes },
              // The other maintenance surface, beside /feeds: reached from /settings, never the sidebar.
              { path: 'import', children: importRoutes },
              { path: 'settings', children: settingsRoutes },
              // 034: /help — the last area. Its pages are emitted by a code generator
              // (`scripts/help/render.ts`), so porting the shell meant porting the generator too; see
              // `help.routes.tsx` for how the generated HTML stays out of every OTHER area's bundle.
              { path: 'help', children: helpRoutes },
              // 036 (issue 207): the outbox's own screen — reached from the toolbar's unsent count,
              // never from the sidebar (it is maintenance, not a data screen — 019's rule).
              { path: 'unsent', children: unsentRoutes },
            ],
          },
        ],
      },
      {
        /**
         * The wildcard. Until every area was ported this landed on `/sign-in` — the honest placeholder
         * for "not built yet" wearing the clothes of "does not exist". 034's last area (`/help`) is
         * mounted above, so that placeholder's reason is gone: every path that can be typed is now
         * either a real route or genuinely not found, and the wildcard is what Angular's final route
         * always was — `NotFound`, reached whether or not a session exists, inside the guard or out of
         * it. `e2e/help.spec.ts` is what asks for this directly: `/help/no-such-page` matches no child
         * of `/help` (`help.routes.tsx` declares none), backtracks past the whole guarded shell, and
         * lands here.
         */
        path: '*',
        lazy: page(() => import('./shared/not-found/not-found')),
      },
    ],
  },
];
